<template>
    <flex-container
        v-if="filteredUsers.length > 0"
        :gap="Size.sm"
        direction="column"
    >
        <select-input
            :items="filteredUsers"
            searchable
            :search-filter="(user, search) => searchFilter(user, search)"
        >
            <template #placeholder>
                Kies je gebruiker
            </template>
            <template #active="{ bindings: activeBindings }">
                <template v-if="activeBindings.value">
                    <span style="display:flex; align-items: center;">
                        <user-avatar
                            :placeholder="activeBindings.value.name.abbreviation"
                            size="md"
                            :url="activeBindings.value.avatar ? resizedToSizeUploadUrl(activeBindings.value.avatar, 'md') : ''"
                            :alt="activeBindings.value.name"
                        />
                        <span>&nbsp;{{ activeBindings.value.name }}</span>
                    </span>
                </template>
                <slot v-else name="placeholder" />
            </template>
            <template #item="{ bindings: itemBindings }">
                <span style="display:flex; align-items: center;">
                    <user-avatar
                        :placeholder="itemBindings.item.name.abbreviation"
                        size="md"
                        :url="itemBindings.item.avatar ? resizedToSizeUploadUrl(itemBindings.item.avatar, 'md') : ''"
                        :alt="itemBindings.item.name"
                    />
                    <span>&nbsp;{{ itemBindings.item.name }}</span>
                </span>
            </template>
        </select-input>
    </flex-container>
</template>

<script setup lang="ts">
    import { User } from '@keyvue/one-api-client';
    import { resizedToSizeUploadUrl } from '@/services/keyvue-api/utils/resize';
    import { FlexContainer, Size } from '@keyvue/one-ui';
    import { computed, onMounted, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';

    const { client, currentUser } = useClient();

    interface Props {
        filter?: (user: any) => boolean,
        withoutCurrentUser?: boolean,
    }

    const props = withDefaults(defineProps<Props>(), {
        filter: undefined,
        withoutCurrentUser: false,
    });

    const userCollection = ref<{ data: User[], total: number }>({ data: [], total: 0 });

    async function loadUsers() {
        if (!currentUser.value || !currentUser.value.company) {
            return;
        }

        const params = {
            'active': true,
            'order[name.firstName]': 'asc',
        };

        userCollection.value = await client.getSubResources(
            currentUser.value.company,
            User,
            'users',
            params,
        );
    }

    const filteredUsers = computed(() => {
        let users = userCollection.value.data;

        if (props.withoutCurrentUser) {
            users = users.filter((user) => !user.id.equals(currentUser.value.id));
        }

        if (props.filter !== undefined) {
            users = users.filter(props.filter);
        }

        return users;
    });

    function searchFilter(user: User | undefined, search: string | null): boolean {
        const term = search?.toLowerCase();

        if (term === undefined) {
            return true;
        }

        return (
            (user?.email?.value?.toLowerCase() ?? '').includes(term)
            || (user?.name?.toString().toLowerCase() ?? '').includes(term)
        );
    }

    onMounted(async () => {
        await loadUsers();
    });
</script>
